import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '@/utils/api-fetch';
import type {
  AdminSiteOption,
  AdminUserRecord,
  CreateAdminUserInput,
  TransferSuperAdminConfirmResult,
  UpdateAdminUserInput,
} from '@/types/admin';

export function useAdminUsers() {
  const [users, setUsers] = useState<AdminUserRecord[]>([]);
  const [sites, setSites] = useState<AdminSiteOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [usersRes, sitesRes] = await Promise.all([
        apiFetch('/admin/users'),
        apiFetch('/admin/sites'),
      ]);

      const usersData = (await usersRes.json()) as { users?: AdminUserRecord[]; error?: string };
      const sitesData = (await sitesRes.json()) as { sites?: AdminSiteOption[]; error?: string };

      if (!usersRes.ok) {
        throw new Error(usersData.error || 'Could not load users');
      }
      if (!sitesRes.ok) {
        throw new Error(sitesData.error || 'Could not load websites');
      }

      setUsers(usersData.users ?? []);
      setSites(sitesData.sites ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load admin data');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const createUser = useCallback(
    async (input: CreateAdminUserInput) => {
      const response = await apiFetch('/admin/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      const data = (await response.json().catch(() => ({}))) as { error?: string };
      if (!response.ok) {
        throw new Error(data.error || 'Could not create user');
      }
      await refresh();
    },
    [refresh]
  );

  const updateUser = useCallback(
    async (id: string, input: UpdateAdminUserInput) => {
      const response = await apiFetch(`/admin/users/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      const data = (await response.json().catch(() => ({}))) as { error?: string };
      if (!response.ok) {
        throw new Error(data.error || 'Could not update user');
      }
      await refresh();
    },
    [refresh]
  );

  const requestSuperAdminTransfer = useCallback(async (targetUserId: string) => {
    const response = await apiFetch('/admin/transfer-super-admin/request', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ targetUserId }),
    });
    const data = (await response.json().catch(() => ({}))) as {
      error?: string;
      targetEmail?: string;
      expiresAt?: string;
    };
    if (!response.ok) {
      throw new Error(data.error || 'Could not send verification code');
    }
    return {
      targetEmail: data.targetEmail ?? '',
      expiresAt: data.expiresAt ?? '',
    };
  }, []);

  const confirmSuperAdminTransfer = useCallback(
    async (targetUserId: string, otp: string): Promise<TransferSuperAdminConfirmResult> => {
      const response = await apiFetch('/admin/transfer-super-admin/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetUserId, otp }),
      });
      const data = (await response.json().catch(() => ({}))) as TransferSuperAdminConfirmResult & {
        error?: string;
      };
      if (!response.ok) {
        throw new Error(data.error || 'Could not complete transfer');
      }
      await refresh();
      return { actor: data.actor };
    },
    [refresh]
  );

  return {
    users,
    sites,
    loading,
    error,
    refresh,
    createUser,
    updateUser,
    requestSuperAdminTransfer,
    confirmSuperAdminTransfer,
  };
}
